import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtUsd, fmtSigned } from "@/lib/journal";

export const MarkPriceDialog = ({ trade, onOpenChange, onSaved }) => {
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (trade) setPrice(trade.manual_price != null ? String(trade.manual_price) : "");
  }, [trade]);

  if (!trade) return null;

  const p = parseFloat(price);
  const value = Number.isFinite(p) ? p * trade.volume : null;
  const pnl = value != null ? value - trade.cost_basis : null;

  const save = async (clear = false) => {
    if (!clear && !Number.isFinite(p)) return toast.error("Enter a valid mark price");
    setSaving(true);
    try {
      await api.put(`/trades/${trade.id}`, { manual_price: clear ? 0 : p });
      toast.success(clear ? "Manual mark cleared" : `Mark price set to ${fmtUsd(p, 4)}`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set mark price");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!trade} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-dialog" className="rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Mark Price <span className="text-[#C58361]">/ {trade.asset}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Set your own valuation for positions the price feed can't value.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="grid grid-cols-3 gap-3 border border-[#1F1F1F] bg-[#050505] p-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Volume</p>
              <p className="mt-1 font-mono text-sm text-white">{trade.volume}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Avg Entry</p>
              <p className="mt-1 font-mono text-sm text-white">{fmtUsd(trade.entry_price, 4)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Cost Basis</p>
              <p className="mt-1 font-mono text-sm text-white">{fmtUsd(trade.cost_basis)}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Mark Price (USD)</Label>
            <Input
              data-testid="mark-price-input"
              type="number" step="any" value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
            />
          </div>

          <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="mark-preview">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Position Value / Unrealized</p>
            <p className="mt-2 font-mono text-xl text-white">
              {value == null ? "—" : fmtUsd(value)}
              {pnl != null && (
                <span className={`ml-3 text-sm ${pnl >= 0 ? "text-[#4ADE80]" : "text-[#EF4444]"}`}>
                  {fmtSigned(pnl)}
                </span>
              )}
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              data-testid="mark-save-btn"
              onClick={() => save(false)}
              disabled={saving}
              className="flex-1 rounded-none bg-[#C58361] py-5 font-medium text-black hover:bg-[#A86B4C]"
            >
              {saving ? "Saving…" : "Set Mark Price"}
            </Button>
            {trade.manual_price != null && (
              <Button
                data-testid="mark-clear-btn"
                onClick={() => save(true)}
                disabled={saving}
                className="rounded-none border border-[#1F1F1F] bg-transparent px-4 py-5 text-xs text-zinc-400 hover:bg-[#111111] hover:text-white"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
